import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { User, Calendar, Link as LinkIcon, Building2, Clock, CheckCircle, AlertCircle, CheckSquare, FileText, BarChart3, ExternalLink, Download, FileImage, ArrowRight, Eye, X, XCircle, Info } from 'lucide-react';
import { collection, query, where, getDocs, getDoc, doc } from 'firebase/firestore';
import { db } from '../lib/firebase';
import type { Collab, Influencer, TimelineItem, CollabDocument, CollabStats } from '../lib/firebase';
import { Dialog } from '@headlessui/react';

const SOCIAL_PLATFORMS = {
  Instagram: {
    icon: 'https://cdn.simpleicons.org/instagram',
    color: 'text-pink-500',
  },
  YouTube: {
    icon: 'https://cdn.simpleicons.org/youtube',
    color: 'text-red-500',
  },
  TikTok: {
    icon: 'https://cdn.simpleicons.org/tiktok',
    color: 'text-purple-500',
  },
  Twitch: {
    icon: 'https://cdn.simpleicons.org/twitch',
    color: 'text-violet-500',
  },
  Snapchat: {
    icon: 'https://cdn.simpleicons.org/snapchat',
    color: 'text-yellow-400',
  },
};

export default function PublicInfluencerView() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [influencer, setInfluencer] = useState<Influencer | null>(null);
  const [collabs, setCollabs] = useState<Collab[]>([]);
  const [timelineItems, setTimelineItems] = useState<TimelineItem[]>([]);
  const [documents, setDocuments] = useState<CollabDocument[]>([]);
  const [stats, setStats] = useState<CollabStats[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isErrorModalOpen, setIsErrorModalOpen] = useState(false);
  const [isDebugModalOpen, setIsDebugModalOpen] = useState(false);
  const [selectedCollab, setSelectedCollab] = useState<Collab | null>(null); 
  const [activeTab, setActiveTab] = useState('collaborations');
  const [debugInfo, setDebugInfo] = useState<any>({});

  useEffect(() => {
    if (token) {
      loadInfluencerData();
    }
  }, [token]);

  const loadInfluencerData = async () => {
    try {
      console.log('Chargement des données pour le token:', token);
      setIsLoading(true);
      setDebugInfo({
        step: 'init',
        token,
        timestamp: new Date().toISOString()
      });
      setError(null);

      // Rechercher l'influenceur par token
      const influencersQuery = query(
        collection(db, 'influencers'),
        where('publicToken', '==', token),
        where('publicLinkEnabled', '==', true)
      );
      
      const influencersSnapshot = await getDocs(influencersQuery);
      
      setDebugInfo(prev => ({
        ...prev,
        step: 'influencer-query',
        influencersFound: !influencersSnapshot.empty,
        influencersCount: influencersSnapshot.docs.length,
        timestamp: new Date().toISOString()
      }));
      
      if (influencersSnapshot.empty) {
        console.error('Aucun influenceur trouvé avec ce token:', token);
        setError('Cet influenceur n\'existe pas ou n\'est plus disponible.');
        setDebugInfo({
          token,
          error: 'Aucun influenceur trouvé',
          timestamp: new Date().toISOString()
        });
        setIsLoading(false);
        return;
      }
      
      const influencerData = { 
        id: influencersSnapshot.docs[0].id, 
        ...influencersSnapshot.docs[0].data() 
      } as Influencer;
      
      console.log('Influenceur trouvé:', influencerData);
      setInfluencer(influencerData);

      setDebugInfo(prev => ({
        ...prev,
        step: 'influencer-found',
        influencerId: influencerData.id,
        influencerName: influencerData.fullName,
        timestamp: new Date().toISOString()
      }));
      
      try {
        // Récupérer toutes les collaborations
        const allCollabsQuery = query(collection(db, 'collabs'));
        const allCollabsSnapshot = await getDocs(allCollabsQuery);
        const allCollabs = allCollabsSnapshot.docs.map(doc => ({ 
          id: doc.id, 
          ...doc.data() 
        }));
        
        setDebugInfo(prev => ({
          ...prev,
          step: 'collabs-query',
          allCollabsCount: allCollabs.length,
          timestamp: new Date().toISOString()
        }));
        
        // Filtrer les collaborations qui contiennent cet influenceur
        const influencerCollabs = allCollabs.filter(collab => {
          // Vérifier si l'ID de l'influenceur est dans les linkedPublicInfluencerIds
          if (collab.linkedPublicInfluencerIds && collab.linkedPublicInfluencerIds.includes(influencerData.id!)) {
            return true;
          }
          
          // Vérifier par nom d'influenceur
          const normalizedName = influencerData.fullName.trim().toLowerCase();
          return collab.influencers && 
            collab.influencers.some(inf => {
              const infName = (inf.fullName || '').trim().toLowerCase();
              return infName === normalizedName;
            });
        });
        
        console.log(`Collaborations trouvées pour ${influencerData.fullName}:`, influencerCollabs.length);

        setDebugInfo(prev => ({
          ...prev,
          step: 'collabs-filtered',
          influencerCollabsCount: influencerCollabs.length,
          timestamp: new Date().toISOString()
        }));
        
        // Trier les collaborations par date (plus récentes en premier)
        influencerCollabs.sort((a, b) => {
          // Convertir les dates en timestamps pour la comparaison
          const dateA = a.date ? 
            (typeof a.date === 'object' && 'seconds' in a.date) ? 
              a.date.seconds * 1000 : 
              new Date(a.date).getTime() : 
            0;
          
          const dateB = b.date ? 
            (typeof b.date === 'object' && 'seconds' in b.date) ? 
              b.date.seconds * 1000 : 
              new Date(b.date).getTime() : 
            0;
          
          // Tri décroissant (plus récent en premier)
          return dateB - dateA;
        });
        
        // Mettre à jour l'état avec les collaborations trouvées
        setCollabs(influencerCollabs);

        setDebugInfo(prev => ({
          ...prev,
          step: 'collabs-set',
          collabsCount: influencerCollabs.length,
          timestamp: new Date().toISOString()
        }));
        
        // Récupérer les éléments de timeline, documents et stats pour ces collaborations
        if (influencerCollabs.length > 0) {
          try {
            // Récupérer tous les éléments de timeline
            const timelineQuery = query(collection(db, 'timelineItems'));
            const timelineSnapshot = await getDocs(timelineQuery);
            const allTimelineItems = timelineSnapshot.docs.map(doc => ({
              id: doc.id,
              ...doc.data()
            })) as TimelineItem[];
            
            // Filtrer les éléments de timeline pour les collaborations de l'influenceur
            const filteredTimelineItems = allTimelineItems.filter(item => 
              influencerCollabs.some(collab => collab.id === item.collabId)
            );
            
            // Ajouter les noms des collaborations pour l'affichage
            const enrichedTimelineItems = filteredTimelineItems.map(item => {
              const collab = influencerCollabs.find(c => c.id === item.collabId);
              return {
                ...item,
                collabName: collab?.name || '',
                collabBrand: collab?.brand || ''
              };
            });
            
            setTimelineItems(enrichedTimelineItems);
            console.log('Timeline items loaded:', enrichedTimelineItems.length);

            setDebugInfo(prev => ({
              ...prev,
              step: 'timeline-loaded',
              timelineItemsCount: enrichedTimelineItems.length,
              timestamp: new Date().toISOString()
            }));
            
            // Récupérer les documents
            const documentsQuery = query(collection(db, 'collabDocuments'));
            const documentsSnapshot = await getDocs(documentsQuery);
            const allDocuments = documentsSnapshot.docs.map(doc => ({
              id: doc.id,
              ...doc.data()
            })) as CollabDocument[];
            
            // Filtrer les documents pour les collaborations de l'influenceur et visibles
            const filteredDocuments = allDocuments.filter(doc => 
              doc.visibleForBrand && influencerCollabs.some(collab => collab.id === doc.collabId)
            );
            
            setDocuments(filteredDocuments);
            console.log('Documents loaded:', filteredDocuments.length);

            setDebugInfo(prev => ({
              ...prev,
              step: 'documents-loaded',
              documentsCount: filteredDocuments.length,
              timestamp: new Date().toISOString()
            }));
            
            // Récupérer les statistiques
            const statsQuery = query(collection(db, 'collabStats'));
            const statsSnapshot = await getDocs(statsQuery);
            const allStats = statsSnapshot.docs.map(doc => ({
              id: doc.id,
              ...doc.data()
            })) as CollabStats[];
            
            // Filtrer les statistiques pour les collaborations de l'influenceur
            const filteredStats = allStats.filter(stat => 
              influencerCollabs.some(collab => collab.id === stat.collabId)
            );
            
            setStats(filteredStats);
            console.log('Stats loaded:', filteredStats.length);

            setDebugInfo(prev => ({
              ...prev,
              step: 'stats-loaded',
              statsCount: filteredStats.length,
              timestamp: new Date().toISOString()
            }));
            
          } catch (error) {
            console.error('Erreur lors de la récupération des données associées:', error);
          }
        }
        
      } catch (collabsError) {
        console.error('Erreur lors de la récupération des collaborations:', collabsError);
        // Ne pas bloquer le chargement si les collaborations ne sont pas trouvées
      }
      
      setDebugInfo({
        token,
        influencerId: influencerData.id,
        influencerName: influencerData.fullName,
        collabsCount: collabs.length,
        timelineCount: timelineItems.length,
        documentsCount: documents.length,
        timestamp: new Date().toISOString()
      });
      
    } catch (error) {
      console.error('Error loading influencer data:', error);
      const errorMessage = error instanceof Error ? error.message : 'Erreur inconnue';
      setError(`Une erreur est survenue lors du chargement des données: ${errorMessage}`);
      setDebugInfo({
        token,
        error: error instanceof Error ? error.message : 'Erreur inconnue',
        timestamp: new Date().toISOString()
      });
    } finally {
      setIsLoading(false);
    }
  };

  const formatDate = (date: Date | { seconds: number } | string | undefined) => {
    if (!date) return 'Date non définie';
    
    if (typeof date === 'string') {
      return new Date(date).toLocaleDateString('fr-FR', {
        day: 'numeric',
        month: 'long',
        year: 'numeric'
      });
    }
    
    if (typeof date === 'object' && date !== null && 'seconds' in date) {
      return new Date(date.seconds * 1000).toLocaleDateString('fr-FR', {
        day: 'numeric',
        month: 'long',
        year: 'numeric'
      });
    }
    
    return new Date(date).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const formatCurrency = (amount: string | number) => {
    const num = typeof amount === 'string' ? parseFloat(amount) : amount;
    if (isNaN(num)) return '0 €';
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR'
    }).format(num);
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'done':
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'in_progress':
        return <Clock className="w-5 h-5 text-yellow-500" />;
      case 'late':
        return <AlertCircle className="w-5 h-5 text-red-500" />;
      default:
        return <CheckSquare className="w-5 h-5 text-gray-400" />;
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'done':
        return 'Terminé';
      case 'in_progress':
        return 'En cours';
      case 'late':
        return 'En retard';
      default:
        return 'À faire';
    }
  };

  const getStatusClass = (status: string) => {
    switch (status) {
      case 'done':
        return 'bg-green-100 text-green-800';
      case 'in_progress':
        return 'bg-yellow-100 text-yellow-800';
      case 'late':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getCampaignStatus = (campaign: Collab) => {
    if (campaign.invoice_sent || campaign.payment_received) return { label: 'Terminée', className: 'bg-green-500/20 text-green-300 border-green-500/20' };
    if (campaign.quote_sent || campaign.contract_signed) return { label: 'En cours', className: 'bg-yellow-500/20 text-yellow-300 border-yellow-500/20' };
    return { label: 'En attente', className: 'bg-blue-500/20 text-blue-300 border-blue-500/20' };
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (error || !influencer) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="max-w-lg w-full bg-white rounded-xl shadow-lg p-8 text-center">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Lien non valide</h1>
          <p className="text-gray-600 mb-6">
            {error || "Cet influenceur n'existe pas ou n'est plus disponible."}
          </p>
          <button
            onClick={() => navigate('/')}
            className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
          >
            Retour à l'accueil
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="flex justify-between items-center py-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-purple-600 to-indigo-600 flex items-center justify-center">
                <User className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-900">{influencer.fullName}</h1>
                <p className="text-sm text-gray-500">Profil influenceur • {influencer.agencyName || 'Le Suivi'}</p>
              </div>
            </div>
            <button 
              onClick={() => setIsDebugModalOpen(true)}
              className="absolute top-2 right-2 p-1 text-gray-400 hover:text-gray-600"
              title="Debug Info"
            >
              <Info className="w-4 h-4" />
            </button>
            <div className="text-sm text-gray-500">
              Dernière mise à jour: {formatDate(influencer.updatedAt)}
            </div>
          </div>
        </div>
      </header>

      {/* Main content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Info cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
            <h2 className="text-lg font-medium text-gray-900 mb-4">Influenceur</h2>
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <User className="w-5 h-5 text-indigo-600" />
                <span className="text-gray-700">{influencer.fullName}</span>
              </div>
              {influencer.platforms.map((platform, index) => {
                return (
                  <div key={index} className="flex items-center gap-3">
                    <img 
                      src={SOCIAL_PLATFORMS[platform.name as keyof typeof SOCIAL_PLATFORMS]?.icon || 'https://cdn.simpleicons.org/message'}
                      alt={platform.name}
                      className={`w-5 h-5 ${SOCIAL_PLATFORMS[platform.name as keyof typeof SOCIAL_PLATFORMS]?.color || 'text-indigo-600'}`}
                    />
                    <span className="text-gray-700">@{platform.handle} • {platform.audience}</span>
                  </div>
                );
              })}
            </div>
          </div>
          
          <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
            <h2 className="text-lg font-medium text-gray-900 mb-4">Collaborations</h2>
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-indigo-100 flex items-center justify-center">
                <span className="text-xl font-bold text-indigo-600">{collabs.length}</span>
              </div>
              <div>
                <p className="text-gray-700">{collabs.length} collaboration(s)</p>
                <p className="text-sm text-gray-500">
                  {collabs.filter(c => c.payment_received).length} terminée(s) • {collabs.filter(c => !c.payment_received).length} en cours
                </p>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
            <h2 className="text-lg font-medium text-gray-900 mb-4">Agence</h2>
            <div className="flex items-center gap-3">
             
            </div>
            {influencer.internalReferent && (
              <div className="flex items-center gap-3 mt-4">
                <User className="w-5 h-5 text-indigo-600" />
                <span className="text-gray-700">Référent: {influencer.internalReferent}</span>
              </div>
            )}
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 mb-8">
          <div className="border-b border-gray-200">
            <nav className="flex -mb-px">
              {[
  { id: 'collaborations', label: 'Collaborations', icon: LinkIcon },
].map((tab) => {

                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex items-center px-6 py-4 font-medium text-sm border-b-2 ${
                      activeTab === tab.id
                        ? 'border-indigo-600 text-indigo-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    <Icon className="w-5 h-5 mr-2" />
                    {tab.label}
                  </button>
                );
              })}
            </nav>
          </div>

          <div className="p-6">
            {/* Collaborations Tab */}
            {activeTab === 'collaborations' && (
              <div>
                <h2 className="text-lg font-medium text-gray-900 mb-6">Historique des collaborations</h2>
                
                {collabs.length === 0 ? (
                  <div className="text-center py-12">
                    <LinkIcon className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-500">Aucune collaboration trouvée pour {influencer.fullName}.</p>
                    <p className="text-gray-400 text-sm mt-2">
                      Les collaborations apparaîtront ici lorsqu'elles seront rendues publiques.
                    </p>
                  </div>
                ) : (
                  <div className="space-y-6">
                    {collabs.map((collab) => (
                      <div 
                        key={collab.id} 
                        className={`bg-white rounded-lg border border-gray-200 p-4 shadow-sm transition-all ${
                          collab.linkToken && collab.linkActive ? 'cursor-pointer hover:shadow-md hover:border-indigo-200 hover:bg-indigo-50/30' : 'cursor-pointer hover:bg-gray-50'
                        }`} 
                        onClick={() => {
                          if (collab.linkToken && collab.linkActive) {
                            // Utiliser window.location.href pour une redirection complète
                            window.location.href = `/suivi/${collab.linkToken}?influencer=${influencer.fullName}`;
                          } else {
                            setSelectedCollab(collab);
                            setIsErrorModalOpen(true);
                          }
                        }}
                      >
                        <div className="flex items-center justify-between mb-4">
                          <div>
                            <h3 className="text-lg font-medium text-gray-900 flex items-center">
                              <span>{collab.name}</span>
                              {collab.linkToken && collab.linkActive && (
                                <span className="ml-2 text-xs text-indigo-600 bg-indigo-50 px-2 py-0.5 rounded-full flex items-center">
                                  <Eye className="w-3 h-3 mr-1" />
                                  Détails disponibles
                                </span>
                              )}
                            </h3>
                            <div className="flex items-center gap-2 mt-1">
                              <Building2 className="w-4 h-4 text-gray-500" />
                              <span className="text-gray-600">{collab.brand}</span>
                            </div>
                          </div>
                          <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium border ${getCampaignStatus(collab).className}`}>
                            {getCampaignStatus(collab).label}
                          </span>
                        </div>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                          <div>
                            <h4 className="text-sm font-medium text-gray-700 mb-2">Détails</h4>
                            <div className="space-y-2">
                              <div className="flex items-center gap-2 text-sm text-gray-600">
                                <Calendar className="w-4 h-4 text-gray-400" />
                                <span>Date: {formatDate(collab.date)}</span>
                              </div>
                              <div className="flex items-center gap-2 text-sm text-gray-600">
                                <User className="w-4 h-4 text-gray-400" />
                                <span>Chef de projet: {collab.manager}</span>
                              </div>
                            </div>
                          </div>
                          
                          <div>
                            <h4 className="text-sm font-medium text-gray-700 mb-2">Livrables</h4>
                            <div className="space-y-2">
                              {collab.influencers
                                .filter(inf => {
                                  if (inf.id === influencer.id) return true;
                                  const normalizedInfName = inf.fullName.trim().toLowerCase();
                                  return normalizedInfName === influencer.fullName.trim().toLowerCase();
                                })
                                .flatMap(inf => inf.deliverables || [])
                                .map((deliverable, index) => (
                                  <div key={index} className="flex items-center justify-between text-sm">
                                    <span className="text-gray-600">{deliverable.type}</span>
                                    <span className="font-medium text-indigo-600">{formatCurrency(deliverable.price)}</span>
                                  </div>
                                ))}
                            </div>
                          </div>
                        </div>

                        <div className="mt-4 pt-4 border-t border-gray-200">
                          <div className="flex items-center justify-between">
                            <span className="text-sm text-indigo-600 font-medium flex items-center gap-1.5">
                              Voir les détails complets
                            </span>
                            <ArrowRight className="w-4 h-4 text-indigo-600" />
                          </div>
                        </div>
                        
                        {/* Timeline items for this collab */}
                        {timelineItems.filter(item => item.collabId === collab.id).length > 0 && (
                          <div className="mt-6 pt-4 border-t border-gray-200">
                            <h4 className="text-sm font-medium text-gray-700 mb-3">Timeline</h4>
                            <div className="space-y-4">
                              {timelineItems
                                .filter(item => item.collabId === collab.id)
                                .sort((a, b) => a.order - b.order)
                                .map((item) => (
                                  <div key={item.id} className="flex items-start gap-3">
                                    <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 ${getStatusClass(item.status)}`}>
                                      {getStatusIcon(item.status)}
                                    </div>
                                    <div>
                                      <div className="flex items-center gap-2">
                                        <span className="font-medium text-gray-900">{item.stepName}</span>
                                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getStatusClass(item.status)}`}>
                                          {getStatusText(item.status)}
                                        </span>
                                      </div>
                                      {item.deadline && (
                                        <div className="text-sm text-gray-500 mt-1">
                                          Échéance: {formatDate(item.deadline)}
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                ))}
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Documents Tab */}
            {activeTab === 'documents' && (
              <div>
                <h2 className="text-lg font-medium text-gray-900 mb-6">Documents et preview</h2>
                
                {documents.length === 0 ? (
                  <div className="text-center py-12">
                    <FileImage className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-500">Aucun document disponible pour le moment.</p>
                    <p className="text-gray-400 text-sm mt-2">
                      Les documents et previews apparaîtront ici lorsqu'ils seront partagés.
                    </p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {documents.map((doc) => (
                      <div key={doc.id} className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm">
                        <div className="flex items-center justify-between mb-3">
                          <FileText className="w-8 h-8 text-indigo-600" />
                          <span className="text-xs text-gray-500">{formatFileSize(doc.size || 0)}</span>
                        </div>
                        <h3 className="font-medium text-gray-900 mb-2 truncate">{doc.name}</h3>
                        <p className="text-sm text-gray-600 mb-3">{doc.type}</p>
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-gray-500">{formatDate(doc.uploadedAt)}</span>
                          <button className="flex items-center gap-1 text-indigo-600 hover:text-indigo-700 text-sm">
                            <Download className="w-4 h-4" />
                            Télécharger
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Stats Tab */}
            {activeTab === 'stats' && (
              <div>
                <h2 className="text-lg font-medium text-gray-900 mb-6">Statistiques</h2>
                
                {stats.length === 0 ? (
                  <div className="text-center py-12">
                    <BarChart3 className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-500">Aucune statistique disponible pour le moment.</p>
                    <p className="text-gray-400 text-sm mt-2">
                      Les statistiques de performance apparaîtront ici lorsqu'elles seront disponibles.
                    </p>
                  </div>
                ) : (
                  <div className="space-y-6">
                    {stats.map((stat) => (
                      <div key={stat.id} className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm">
                        <div className="flex items-center justify-between mb-4">
                          <h3 className="text-lg font-medium text-gray-900">{stat.campaignName}</h3>
                          <span className="text-sm text-gray-500">{formatDate(stat.date)}</span>
                        </div>
                        
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                          <div className="text-center">
                            <div className="text-2xl font-bold text-indigo-600">{stat.views?.toLocaleString() || '0'}</div>
                            <div className="text-sm text-gray-500">Vues</div>
                          </div>
                          <div className="text-center">
                            <div className="text-2xl font-bold text-green-600">{stat.likes?.toLocaleString() || '0'}</div>
                            <div className="text-sm text-gray-500">Likes</div>
                          </div>
                          <div className="text-center">
                            <div className="text-2xl font-bold text-blue-600">{stat.comments?.toLocaleString() || '0'}</div>
                            <div className="text-sm text-gray-500">Commentaires</div>
                          </div>
                          <div className="text-center">
                            <div className="text-2xl font-bold text-purple-600">{stat.shares?.toLocaleString() || '0'}</div>
                            <div className="text-sm text-gray-500">Partages</div>
                          </div>
                        </div>
                        
                        {stat.engagementRate && (
                          <div className="mt-4 pt-4 border-t border-gray-200">
                            <div className="flex items-center justify-between">
                              <span className="text-sm text-gray-600">Taux d'engagement</span>
                              <span className="text-lg font-semibold text-indigo-600">{stat.engagementRate}%</span>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-white border-t border-gray-200 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="flex items-center mb-4 md:mb-0">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-purple-600 to-indigo-600 flex items-center justify-center flex-shrink-0">
                <User className="w-5 h-5 text-white" />
              </div>
              <span className="ml-2 text-lg font-semibold text-gray-900">{influencer.agencyName || 'Le Suivi'}</span>
            </div>
            <p className="text-sm text-gray-500">
              Ce lien de suivi est privé et confidentiel. Merci de ne pas le partager.
            </p>
          </div>
        </div>
      </footer>
      
      {/* Modal d'erreur pour les collaborations sans lien public */}
      <Dialog
        open={isErrorModalOpen}
        onClose={() => setIsErrorModalOpen(false)} 
        className="fixed inset-0 z-[9999] overflow-y-auto"
      >
        <div className="flex items-center justify-center min-h-screen p-4">
          <Dialog.Overlay className="fixed inset-0 bg-black bg-opacity-30" />
          <div className="relative bg-white rounded-xl max-w-md w-full mx-4 p-8 border border-gray-200 shadow-xl animate-fadeIn">
            <button
              onClick={() => setIsErrorModalOpen(false)}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
            <div className="flex flex-col items-center text-center">
              <div className="w-16 h-16 rounded-full bg-purple-100 flex items-center justify-center mb-4">
                <XCircle className="w-8 h-8 text-red-500" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                Détails non disponibles
              </h3>
              <p className="text-gray-600 mb-4">
                Cette collaboration n'a pas encore de lien public activé.
                {selectedCollab && (
                  <span className="block mt-2 font-medium">
                    {selectedCollab.name} - {selectedCollab.brand}
                  </span>
                )}
              </p>
              <p className="text-gray-500 text-sm mb-8">
                Nous allons générer un lien temporaire pour vous permettre d'accéder aux détails.
              </p>
              <button
                onClick={() => {
                  setIsErrorModalOpen(false);
                  if (selectedCollab && selectedCollab.id) {
                    // Générer un token temporaire basé sur l'ID de la collaboration et ajouter le nom de l'influenceur
                    const tempToken = `temp-${selectedCollab.id.substring(0, 8)}`;
                    window.location.href = `/suivi/${tempToken}?influencer=${influencer.fullName}`;
                  }
                }}
                className="px-6 py-2.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors font-medium w-full sm:w-auto"
              >
                Accéder aux détails
              </button>
            </div>
          </div>
        </div>
      </Dialog>
      
      {/* Debug Modal */}
      <Dialog
        open={isDebugModalOpen}
        onClose={() => setIsDebugModalOpen(false)}
        className="fixed inset-0 z-[9999] overflow-y-auto"
      >
        <div className="flex items-center justify-center min-h-screen p-4">
          <Dialog.Overlay className="fixed inset-0 bg-black bg-opacity-30" />
          <div className="relative bg-white rounded-xl max-w-md w-full mx-4 p-8 border border-gray-200 shadow-xl">
            <button
              onClick={() => setIsDebugModalOpen(false)}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
            <div className="flex flex-col items-center text-center">
              <div className="w-16 h-16 rounded-full bg-blue-100 flex items-center justify-center mb-4">
                <Info className="w-8 h-8 text-blue-500" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                Informations de débogage
              </h3>
              <div className="w-full text-left bg-gray-100 p-4 rounded-lg overflow-auto max-h-96">
                <pre className="text-xs text-gray-800 whitespace-pre-wrap">
                  {JSON.stringify(debugInfo, null, 2)}
                </pre>
              </div>
              <button
                onClick={() => setIsDebugModalOpen(false)}
                className="mt-4 px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition-colors"
              >
                Fermer
              </button>
            </div>
          </div>
        </div>
      </Dialog>
    </div>
  );
}